import * as Cesium from 'cesium'
import 'cesium/Build/Cesium/Widgets/widgets.css'
import { CRSManager } from './crs-manager'

// Cesium's asynchronous assets are copied to /cesium by vue.config.js. Keep
// the address here so a deployment never relies on Cesium Ion or a third-party
// JavaScript CDN merely to construct the globe.
Cesium.buildModuleUrl.setBaseUrl('/cesium/')

const osmUrl = 'https://tile.openstreetmap.org/'
const imageryCompareUrl = 'https://server.arcgisonline.com/ArcGIS/rest/services/World_Imagery/MapServer/tile/{z}/{y}/{x}'
const selectedColor = '#ffd166'

const asColor = (value, alpha = 1) => Cesium.Color.fromCssColorString(value || '#58a6ff').withAlpha(alpha)

function heightForZoom(zoom) {
  return Math.max(2500, 40075016.68557849 / Math.pow(2, Number(zoom) || 4.6) * 1.4)
}

function zoomForHeight(height) {
  return Math.max(0, Math.min(22, Math.log2(40075016.68557849 * 1.4 / Math.max(1, height))))
}

function transformCoordinates(coordinates, sourceCrs) {
  if (!Array.isArray(coordinates)) return coordinates
  if (typeof coordinates[0] === 'number') return CRSManager.transformCoordinate(coordinates, sourceCrs, 'EPSG:4326')
  return coordinates.map(item => transformCoordinates(item, sourceCrs))
}

function cesiumGeoJson(layer) {
  if (layer.crs === 'EPSG:4326') return layer.data
  return {
    type: 'FeatureCollection',
    features: layer.data.features.map(feature => ({
      ...feature,
      geometry: feature.geometry ? { ...feature.geometry, coordinates: transformCoordinates(feature.geometry.coordinates, layer.crs) } : null
    }))
  }
}

function extentInWgs84(layer) {
  if (!layer || !layer.metadata || !layer.metadata.extent) return null
  const extent = layer.metadata.extent
  if (layer.crs === 'EPSG:4326') return extent
  const southwest = CRSManager.transformCoordinate([extent[0], extent[1]], layer.crs, 'EPSG:4326')
  const northeast = CRSManager.transformCoordinate([extent[2], extent[3]], layer.crs, 'EPSG:4326')
  return [southwest[0], southwest[1], northeast[0], northeast[1]]
}

export class CesiumAdapter {
  constructor(container, mapState, onFeatureClick = () => {}, onPointerMove = () => {}, onMapClick = () => {}) {
    this.container = container
    this.onBasemapStatus = () => {}
    this.osmFailureCount = 0
    this.fallbackBaseLayer = null
    this.osmProvider = new Cesium.OpenStreetMapImageryProvider({
      url: osmUrl,
      maximumLevel: 19,
      credit: '\u00A9 OpenStreetMap contributors'
    })
    this.baseImageryLayer = new Cesium.ImageryLayer(this.osmProvider)
    this.viewer = new Cesium.Viewer(container, {
      animation: false,
      baseLayerPicker: false,
      // Cesium 1.114 uses `baseLayer`. The old `imageryProvider` option is
      // no longer attached to the scene, which resulted in a blue globe with
      // no OSM imagery despite a successful Cesium startup.
      baseLayer: this.baseImageryLayer,
      fullscreenButton: false,
      geocoder: false,
      homeButton: false,
      infoBox: false,
      navigationHelpButton: false,
      sceneModePicker: false,
      selectionIndicator: false,
      timeline: false,
      terrainProvider: new Cesium.EllipsoidTerrainProvider()
    })
    this.scene = this.viewer.scene
    this.dataSources = new Map()
    this.remoteLayers = new Map()
    this.layerRefs = new Map()
    this.layerModels = new Map()
    this.measurementDataSource = new Cesium.CustomDataSource('gw-measurement')
    this.viewer.dataSources.add(this.measurementDataSource)
    this.selected = { layerId: null, featureIds: [] }
    this.compareLayer = null
    this.onMapChange = () => {}
    this.onFeatureClick = onFeatureClick
    this.onPointerMove = onPointerMove
    this.onMapClick = onMapClick
    this.osmProvider.errorEvent.addEventListener(() => this.handleOsmError())
    this.handler = new Cesium.ScreenSpaceEventHandler(this.scene.canvas)
    this.handler.setInputAction(movement => this.handleLeftClick(movement), Cesium.ScreenSpaceEventType.LEFT_CLICK)
    this.handler.setInputAction(movement => this.handlePointerMove(movement), Cesium.ScreenSpaceEventType.MOUSE_MOVE)
    this.viewer.camera.setView({
      destination: Cesium.Cartesian3.fromDegrees(mapState.center[0], mapState.center[1], heightForZoom(mapState.zoom))
    })
    this.viewer.camera.moveEnd.addEventListener(() => this.onMapChange(this.getMapState()))
  }

  setMapChangeHandler(handler) { this.onMapChange = handler }
  setBasemapStatusHandler(handler) { this.onBasemapStatus = typeof handler === 'function' ? handler : () => {} }
  isReady() { return !this.viewer.isDestroyed() }
  resize() { if (this.isReady()) this.viewer.resize() }
  destroy() {
    if (this.handler && !this.handler.isDestroyed()) this.handler.destroy()
    if (this.viewer && !this.viewer.isDestroyed()) this.viewer.destroy()
  }

  handleLeftClick(movement) {
    const picked = this.scene.pick(movement.position)
    const entity = picked && picked.id
    if (entity && entity.__geoWorkbenchLayerId && entity.__geoWorkbenchFeatureId) {
      this.onFeatureClick({ layerId: entity.__geoWorkbenchLayerId, featureId: entity.__geoWorkbenchFeatureId })
    }
    const lngLat = this.positionToLngLat(movement.position)
    if (lngLat) this.onMapClick({ lngLat, point: movement.position })
  }

  handlePointerMove(movement) {
    const lngLat = this.positionToLngLat(movement.endPosition)
    if (lngLat) this.onPointerMove(lngLat)
  }

  positionToLngLat(position) {
    const cartesian = this.viewer.camera.pickEllipsoid(position, this.scene.globe.ellipsoid)
    if (!cartesian) return null
    const cartographic = Cesium.Cartographic.fromCartesian(cartesian)
    return { lng: Cesium.Math.toDegrees(cartographic.longitude), lat: Cesium.Math.toDegrees(cartographic.latitude) }
  }

  syncLayers(layers) {
    if (!this.isReady()) return
    const activeIds = new Set(layers.map(layer => layer.id))
    ;[...this.dataSources.keys()].filter(id => !activeIds.has(id)).forEach(id => this.removeVectorLayer(id))
    ;[...this.remoteLayers.keys()].filter(id => !activeIds.has(id)).forEach(id => this.removeRemoteLayer(id))
    layers.forEach(layer => {
      if (layer.type === 'vector' && layer.data) { this.layerModels.set(layer.id, layer); this.addOrUpdateVector(layer) }
      else if (['xyz', 'wms', 'wmts', 'tile'].includes(layer.type) && !layer.system) this.addOrUpdateRemoteLayer(layer)
      else if (layer.system) this.setBasemapVisibility(layer.visible)
    })
    this.applySelection(this.selected)
  }

  setBasemapVisibility(visible) {
    if (this.baseImageryLayer) this.baseImageryLayer.show = visible
    if (this.fallbackBaseLayer) this.fallbackBaseLayer.show = visible
  }

  handleOsmError() {
    this.osmFailureCount += 1
    // Cesium retries individual tiles. Only declare the provider unavailable
    // after repeated failures, then keep the canvas useful with a labelled
    // satellite fallback underneath OSM.
    if (this.osmFailureCount < 3 || this.fallbackBaseLayer || !this.isReady()) return
    const provider = new Cesium.UrlTemplateImageryProvider({
      url: imageryCompareUrl,
      credit: 'Esri World Imagery',
      maximumLevel: 19
    })
    this.fallbackBaseLayer = new Cesium.ImageryLayer(provider)
    this.viewer.imageryLayers.add(this.fallbackBaseLayer, 0)
    this.fallbackBaseLayer.show = this.baseImageryLayer.show
    this.onBasemapStatus({
      type: 'warning',
      message: 'OpenStreetMap tiles are temporarily unavailable. Satellite imagery is being shown as a fallback.'
    })
  }

  async addOrUpdateVector(layer) {
    const existing = this.dataSources.get(layer.id)
    if (existing && this.layerRefs.get(layer.id) === layer.data) {
      existing.show = layer.visible
      this.applyVectorStyle(existing, layer)
      return
    }
    if (existing) this.removeVectorLayer(layer.id)
    try {
      const source = await Cesium.GeoJsonDataSource.load(cesiumGeoJson(layer), { clampToGround: false })
      if (!this.isReady()) return
      source.name = layer.name
      source.show = layer.visible
      source.entities.values.forEach(entity => {
        const property = entity.properties && entity.properties.__gwFeatureId
        entity.__geoWorkbenchLayerId = layer.id
        entity.__geoWorkbenchFeatureId = String(property && property.getValue ? property.getValue(Cesium.JulianDate.now()) : entity.id)
      })
      this.viewer.dataSources.add(source)
      this.dataSources.set(layer.id, source)
      this.layerRefs.set(layer.id, layer.data)
      this.applyVectorStyle(source, layer)
      this.applySelection(this.selected)
    } catch (error) {
      // Import and processing errors are surfaced at their source; failing to
      // paint one data source must not take down the map canvas.
      console.error('Could not render vector layer on the Cesium globe.', error)
    }
  }

  applyVectorStyle(source, layer) {
    source.entities.values.forEach(entity => {
      const selected = this.selected.layerId === layer.id && this.selected.featureIds.includes(String(entity.__geoWorkbenchFeatureId))
      if (entity.point) {
        entity.point.color = asColor(selected ? selectedColor : layer.style.point.color, layer.style.point.opacity * layer.opacity)
        entity.point.outlineColor = asColor(layer.style.point.outlineColor)
        entity.point.outlineWidth = layer.style.point.outlineWidth
        entity.point.pixelSize = layer.style.point.size * 1.8
      }
      if (entity.polyline) {
        entity.polyline.material = asColor(selected ? selectedColor : layer.style.line.color, layer.style.line.opacity * layer.opacity)
        entity.polyline.width = layer.style.line.width
      }
      if (entity.polygon) {
        entity.polygon.material = asColor(selected ? selectedColor : layer.style.polygon.color, layer.style.polygon.opacity * layer.opacity)
        entity.polygon.outline = true
        entity.polygon.outlineColor = asColor(layer.style.polygon.outlineColor)
      }
    })
  }

  addOrUpdateRemoteLayer(layer) {
    const existing = this.remoteLayers.get(layer.id)
    if (existing) { existing.show = layer.visible; existing.alpha = layer.opacity; return }
    let provider
    if (layer.type === 'wms') {
      provider = new Cesium.WebMapServiceImageryProvider({ url: layer.source.endpoint, layers: layer.source.layers, parameters: { transparent: true, format: layer.source.format || 'image/png' } })
    } else provider = new Cesium.UrlTemplateImageryProvider({ url: layer.source.url })
    const imageryLayer = this.viewer.imageryLayers.addImageryProvider(provider)
    imageryLayer.show = layer.visible
    imageryLayer.alpha = layer.opacity
    this.remoteLayers.set(layer.id, imageryLayer)
  }

  removeVectorLayer(layerId) {
    const source = this.dataSources.get(layerId)
    if (source) this.viewer.dataSources.remove(source, true)
    this.dataSources.delete(layerId)
    this.layerRefs.delete(layerId)
    this.layerModels.delete(layerId)
  }

  removeRemoteLayer(layerId) {
    const layer = this.remoteLayers.get(layerId)
    if (layer) this.viewer.imageryLayers.remove(layer, true)
    this.remoteLayers.delete(layerId)
  }

  fitLayer(layer) {
    const bounds = extentInWgs84(layer)
    if (!bounds) return
    this.viewer.camera.flyTo({ destination: Cesium.Rectangle.fromDegrees(bounds[0], bounds[1], bounds[2], bounds[3]), duration: 0.65 })
  }

  zoomIn() { this.viewer.camera.zoomIn(this.viewer.camera.positionCartographic.height * 0.28) }
  zoomOut() { this.viewer.camera.zoomOut(this.viewer.camera.positionCartographic.height * 0.35) }
  resetNorth() { this.viewer.camera.setView({ orientation: { heading: 0, pitch: -Cesium.Math.PI_OVER_TWO, roll: 0 } }) }
  flyHome() { this.viewer.camera.flyHome(0.65) }

  setSceneMode(mode) {
    if (mode === '2d') this.scene.morphTo2D(0.55)
    else this.scene.morphTo3D(0.55)
  }

  setSwipe(enabled, position = 0.5) {
    if (enabled && !this.compareLayer) {
      this.compareLayer = this.viewer.imageryLayers.addImageryProvider(new Cesium.UrlTemplateImageryProvider({
        url: imageryCompareUrl,
        credit: 'Esri World Imagery'
      }))
      this.compareLayer.splitDirection = Cesium.SplitDirection.LEFT
    }
    if (!enabled && this.compareLayer) {
      this.viewer.imageryLayers.remove(this.compareLayer, true)
      this.compareLayer = null
    }
    this.scene.splitPosition = Math.max(0, Math.min(1, Number(position) || 0.5))
  }

  setMeasurement(measurement) {
    if (!this.measurementDataSource) return
    const coordinates = measurement && measurement.coordinates || []
    this.measurementDataSource.entities.removeAll()
    if (!coordinates.length) return
    const positions = coordinates.map(coordinate => Cesium.Cartesian3.fromDegrees(coordinate[0], coordinate[1], 10))
    positions.forEach(position => this.measurementDataSource.entities.add({
      position,
      point: { color: Cesium.Color.fromCssColorString(selectedColor), pixelSize: 8, outlineColor: Cesium.Color.fromCssColorString('#17212b'), outlineWidth: 2 }
    }))
    if (positions.length > 1) this.measurementDataSource.entities.add({
      polyline: { positions, width: 2, material: Cesium.Color.fromCssColorString(selectedColor) }
    })
    if (measurement.type === 'area' && positions.length > 2) this.measurementDataSource.entities.add({
      polygon: { hierarchy: positions, material: Cesium.Color.fromCssColorString(selectedColor).withAlpha(0.22), outline: true, outlineColor: Cesium.Color.fromCssColorString(selectedColor) }
    })
  }

  locateUser() {
    if (!navigator.geolocation) return Promise.reject(new Error('This browser does not provide location services.'))
    return new Promise((resolve, reject) => {
      navigator.geolocation.getCurrentPosition(position => {
        const { longitude, latitude } = position.coords
        this.viewer.camera.flyTo({ destination: Cesium.Cartesian3.fromDegrees(longitude, latitude, Math.max(800, position.coords.accuracy * 8)), duration: 0.85 })
        resolve({ lng: longitude, lat: latitude })
      }, error => reject(new Error(error.message || 'Location permission was not granted.')), { enableHighAccuracy: true, timeout: 10000, maximumAge: 30000 })
    })
  }

  applySelection(selection) {
    this.selected = selection || { layerId: null, featureIds: [] }
    this.dataSources.forEach((source, layerId) => {
      const layer = this.layerModels.get(layerId)
      if (!layer) return
      this.applyVectorStyle(source, layer)
    })
  }

  getMapState() {
    const cartographic = this.viewer.camera.positionCartographic
    return {
      center: [Cesium.Math.toDegrees(cartographic.longitude), Cesium.Math.toDegrees(cartographic.latitude)],
      zoom: zoomForHeight(cartographic.height)
    }
  }
}
